import { FC, Fragment } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import InfoCircle, { InfoCircleVariant } from '../../../../../../../assets/icons/info-circle';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { FormInput } from '../../../../../../../components/input/index';
import { Switch } from '../../../../../../../components/switch/index';
import { Cursor, Tooltip } from '../../../../../../../components/tooltip/index';
import { expirationPeriodOptions } from '../../../../constants/add-template-form-options.constant';
import { TemplateCategory } from '../../../../enums/template.const';
import { useCheckTemplateName } from '../../../../hooks/use-check-template-name.hook';
import { StepContentProps } from '../../../../types/template.types';
import { CategoryCombobox, EquityPlanCombobox, SubCategoryCombobox } from '../combobox/index';

type StepOneProps = Pick<
  StepContentProps,
  | 'clearErrors'
  | 'control'
  | 'excludeTemplateId'
  | 'getFieldError'
  | 'getValues'
  | 'setError'
  | 'setValue'
>;

export const StepOne: FC<StepOneProps> = ({
  control,
  setError,
  clearErrors,
  excludeTemplateId,
  getFieldError,
  getValues,
  setValue,
}) => {
  const category = useWatch({ control, name: 'stepOne.category' });
  const subCategory = useWatch({ control, name: 'stepOne.subCategory' });
  const expireDocument = useWatch({ control, name: 'stepOne.expireDocument' });
  const expirationPeriod = useWatch({ control, name: 'stepOne.expirationPeriod' });

  const { checkName } = useCheckTemplateName({
    clearErrors,
    excludeTemplateId,
    getFieldError,
    getValues,
    setError,
  });

  const { field: expireDocumentField } = useController({
    control,
    name: 'stepOne.expireDocument',
  });

  const { field: expirationPeriodField, fieldState: expirationPeriodFieldState } = useController({
    control,
    name: 'stepOne.expirationPeriod',
  });

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <div className="flex flex-col gap-3">
          <span className="text-xs text-gray-500">
            <AppFormattedMessage id="DOCUMENT" />
          </span>
          <FormInput
            control={control}
            name="stepOne.name"
            onChange={checkName}
            placeholder={<AppFormattedMessage id="NAME" />}
            wrapperClassName="min-h-[56px]"
          />
        </div>

        <div className="flex flex-col gap-4">
          <span className="text-xs text-gray-500">
            <AppFormattedMessage id="CLASSIFICATION" />
          </span>

          <div className="flex flex-col gap-1">
            <CategoryCombobox control={control} />
          </div>

          {category && (
            <div className="flex flex-col gap-1">
              <SubCategoryCombobox control={control} disabled={!category} />
            </div>
          )}

          {subCategory && (
            <div className="flex flex-col gap-1">
              <EquityPlanCombobox
                control={control}
                disabled={category !== TemplateCategory.GRANT}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">
              <AppFormattedMessage id="EXPIRATION_OPTIONAL" />
            </span>
            <Tooltip
              className="w-[480px]"
              content={<AppFormattedMessage id="EXPIRATION_TOOLTIP" />}
              cursor={Cursor.POINTER}
              placement="top"
            >
              <button
                className="flex shrink-0 items-center justify-center text-gray-400 hover:text-gray-600"
                type="button"
              >
                <InfoCircle
                  className="size-4 text-gray-400 hover:text-gray-600"
                  iconColor="currentColor"
                  variant={InfoCircleVariant.EVENTS}
                />
              </button>
            </Tooltip>
          </div>
          <div className={'flex flex-col gap-4 rounded-lg border border-gray-100 bg-white p-3'}>
            <div className="flex items-center gap-3">
              <Switch
                checked={expireDocument}
                onChange={(value) => {
                  expireDocumentField.onChange(value);
                  if (!value) {
                    setValue('stepOne.expirationPeriod', undefined);
                  }
                }}
              />
              <span className="text-sm text-gray-700">
                <AppFormattedMessage id="EXPIRE_DOCUMENT" />
              </span>
            </div>

            {expireDocument && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  {expirationPeriodOptions.map((option, index) => {
                    const isSelected = expirationPeriod === option.value;
                    const isLast = index === expirationPeriodOptions.length - 1;
                    return (
                      <Fragment key={option.value}>
                        <button
                          className={twMerge(
                            'h-10 flex-1 px-2 text-[12px] transition-colors',
                            isSelected
                              ? 'bg-brand-700 text-[14px] text-white'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-50',
                          )}
                          onClick={() => expirationPeriodField.onChange(option.value)}
                          type="button"
                        >
                          <AppFormattedMessage id={option.labelKey} />
                        </button>
                        {!isLast && <div aria-hidden className="h-10 w-px shrink-0 bg-gray-200" />}
                      </Fragment>
                    );
                  })}
                </div>
                {expirationPeriodFieldState.error?.message && (
                  <span className="text-xs text-fireside-500">
                    {expirationPeriodFieldState.error.message}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
